import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Seo } from "@/components/Seo";
import { LogOut } from "lucide-react";
import { DashboardSection, TrafficSection } from "./admin/Analytics";
import { ClientsSection } from "./admin/Clients";
import { SubscribersSection } from "./admin/Subscribers";
import { RfqsSection } from "./admin/Rfqs";
import { CampaignsSection } from "./admin/Campaigns";
import { AvailabilitySection } from "./admin/AvailabilitySection";
import { AiSection } from "./admin/AiSection";
import { DocumentsSection } from "./admin/Documents";
import { SeoSection } from "./admin/SeoSection";
import { SettingsSection } from "./admin/SettingsSection";
import { AuditSection } from "./admin/AuditLog";

const inputCls =
  "w-full bg-white border border-midnight/15 px-3 py-2.5 text-sm text-midnight placeholder:text-midnight/35 outline-none focus:border-gold transition-colors";
const labelCls = "block text-xs font-bold uppercase tracking-[0.12em] text-midnight/50 mb-1.5";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm bg-white hairline shadow-medium p-10">
        <div className="flex justify-center mb-8"><Logo /></div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-midnight/40 text-center mb-8">Commercial Portal</p>
        <label className={labelCls}>Email</label>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" className={`${inputCls} mb-4`} />
        <label className={labelCls}>Password</label>
        <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" className={`${inputCls} mb-6`} />
        <button type="submit" disabled={busy} data-testid="admin-login-submit" className="w-full bg-midnight text-white text-sm font-semibold py-3.5 hover:bg-midnight-light transition-colors disabled:opacity-50">
          {busy ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
};

const SECTIONS = [
  ["dashboard", "Dashboard"],
  ["traffic", "Traffic"],
  ["clients", "Clients"],
  ["subscribers", "Subscribers"],
  ["rfqs", "RFQs"],
  ["campaigns", "Campaigns"],
  ["availability", "Availability"],
  ["ai", "AI Conversations"],
  ["documents", "Documents"],
  ["seo", "SEO & Search"],
  ["settings", "Settings"],
  ["audit", "Audit Log"],
];

const SECTION_COMPONENTS = {
  dashboard: DashboardSection,
  traffic: TrafficSection,
  clients: ClientsSection,
  subscribers: SubscribersSection,
  rfqs: RfqsSection,
  campaigns: CampaignsSection,
  availability: AvailabilitySection,
  ai: AiSection,
  documents: DocumentsSection,
  seo: SeoSection,
  settings: SettingsSection,
  audit: AuditSection,
};

const Portal = ({ user, onLogout }) => {
  const [section, setSection] = useState("dashboard");
  const Active = SECTION_COMPONENTS[section];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-dashboard">
      <header className="bg-white/90 backdrop-blur-xl border-b border-midnight/10 sticky top-0 z-40">
        <div className="px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-5">
            <span className="text-xs text-midnight/45 hidden sm:inline">{user.email}</span>
            <button onClick={onLogout} data-testid="admin-logout" className="flex items-center gap-2 text-xs font-medium text-midnight/60 hover:text-gold transition-colors">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        <aside className="hidden md:block w-56 shrink-0 border-r border-midnight/10 bg-white min-h-[calc(100vh-64px)] sticky top-16 self-start" data-testid="admin-sidebar">
          <nav className="py-6">
            {SECTIONS.map(([id, label]) => (
              <button
                key={id}
                onClick={() => setSection(id)}
                data-testid={`admin-nav-${id}`}
                className={`w-full text-left px-6 py-3 text-sm transition-colors border-l-2 ${
                  section === id
                    ? "border-gold text-midnight font-semibold bg-gold/[0.05]"
                    : "border-transparent text-midnight/55 hover:text-midnight hover:bg-[#FAFAFA]"
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
        </aside>

        <div className="md:hidden sticky top-16 z-30 bg-white border-b border-midnight/10 overflow-x-auto">
          <div className="flex gap-1 px-4 py-2 w-max">
            {SECTIONS.map(([id, label]) => (
              <button
                key={id}
                onClick={() => setSection(id)}
                className={`px-3 py-2 text-xs whitespace-nowrap font-medium ${section === id ? "text-midnight border-b-2 border-gold" : "text-midnight/50"}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <main className="flex-1 px-6 lg:px-10 py-8 min-w-0">
          <Active />
        </main>
      </div>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  if (user === undefined) {
    return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center text-xs font-medium tracking-[0.2em] text-midnight/40">LOADING…</div>;
  }

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  return (
    <>
      <Seo title="Admin — Royal Deals FZ-LLC" description="Royal Deals FZ-LLC commercial portal." robots="noindex, nofollow, noarchive" />
      {user ? <Portal user={user} onLogout={logout} /> : <Login onLogin={setUser} />}
    </>
  );
}
